import React from 'react';
import Container from '@v2/shared/components/Container';
import { useSite } from '@v2/shared/lib/site';
import type { FullLayoutProps } from '@v2/shared/lib/types';
import FooterMenu from '@v2/shared/Layouts/Full/Footer/FooterMenu';
import useCurrentYear from '@v2/shared/lib/useCurrentYear';

import AcceptedPaymentMethods from './AcceptedPaymentMethods';
import ResellerRatingsBadge from './ResellerRatingsBadge';
import useFooterMenu from './useFooterMenu';

/**
 * Ported from `domain-overrides/magzoom.com/layouts/full/footer.html`.
 *
 * The two link columns and the ResellerRatings seal across the top of a grey band, and
 * the card marks and the copyright centred under them — the template's two rows.
 *
 * The columns are the shared FooterMenu, as on MagSpot; the seal is MagZoom's own.
 *
 * The seal only sits beside the columns from lg. It is a 228px vendor widget, so splitting
 * the row four ways to fit it gave it 175px at sm and it ran off the page, while leaving
 * the two menus a quarter each — 76px at sm, 164px at md, narrow enough to wrap every
 * entry onto two lines. Below lg it takes its own row under the columns, where the card
 * marks already are, and the three read as the stack the legacy footer is.
 */
/** The year comes from the layout's props — see `FullLayoutProps['copyrightYear']`. */
type Props = Pick<FullLayoutProps, 'copyrightYear'>;

export default function Footer({ copyrightYear }: Props) {
  const site = useSite();
  const menu = useFooterMenu();
  const year = useCurrentYear(copyrightYear);

  return (
    <footer className="mt-auto bg-slate-100">
      <Container className="py-6">
        <div className="grid grid-cols-2 gap-6 lg:grid-cols-4">
          <FooterMenu groups={menu} />

          <div className="col-span-2 flex justify-center">
            <ResellerRatingsBadge />
          </div>
        </div>

        <div className="pt-4 text-center">
          <div className="mb-3">
            <AcceptedPaymentMethods />
          </div>
          <p className="m-0 text-xs text-slate-700">
            &copy; {year} {site.name}. All rights reserved.
          </p>
        </div>
      </Container>
    </footer>
  );
}
